import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

import Header from '../../components/ui/Header';
import ConversationList from './components/ConversationList';
import ChatInterface from './components/ChatInterface';
import ConversationDetails from './components/ConversationDetails';
import {
  getUserConversations,
  getConversationMessages,
  sendMessage,
  sendMessageWithFile,
  markMessagesAsRead,
  subscribeToConversations,
  subscribeToConversationMessages,
  searchMessages
} from '../../services/messagingService';

const MessagingHub = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const conversationSubscription = useRef(null);
  const messageSubscription = useRef(null);

  // Fetch conversations on mount
  useEffect(() => {
    if (!user?.id) return;

    const fetchConversations = async () => {
      setLoading(true);
      const { data, error } = await getUserConversations(user?.id);
      if (!error && data) {
        setConversations(data);
      }
      setLoading(false);
    };

    fetchConversations();

    // Subscribe to conversation updates
    conversationSubscription.current = subscribeToConversations(user?.id, () => {
      fetchConversations();
    });

    return () => {
      conversationSubscription?.current?.unsubscribe();
    };
  }, [user?.id]);

  // Fetch messages when conversation selected
  useEffect(() => {
    if (!selectedConversation?.id) return;

    const fetchMessages = async () => {
      const { data, error } = await getConversationMessages(selectedConversation?.id);
      if (!error && data) {
        setMessages(data);
        // Mark messages as read
        await markMessagesAsRead(selectedConversation?.id, user?.id);
      }
    };

    fetchMessages();

    // Subscribe to new messages
    messageSubscription.current = subscribeToConversationMessages(
      selectedConversation?.id,
      (newMessage, eventType) => {
        if (eventType === 'UPDATE') {
          setMessages(prev => prev?.map(msg => 
            msg?.id === newMessage?.id ? { ...msg, ...newMessage } : msg
          ));
        } else {
          setMessages(prev => [...prev, newMessage]);
          // Mark as read if not sent by current user
          if (newMessage?.sender_id !== user?.id) {
            markMessagesAsRead(selectedConversation?.id, user?.id);
          }
        }
      }
    );

    return () => {
      messageSubscription?.current?.unsubscribe();
    };
  }, [selectedConversation?.id, user?.id]);

  // Handle search
  useEffect(() => {
    if (!searchQuery?.trim() || !selectedConversation?.id) {
      setSearchResults([]);
      return;
    }

    const performSearch = async () => {
      const { data } = await searchMessages(selectedConversation?.id, searchQuery);
      setSearchResults(data || []);
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedConversation?.id]);

  const handleSendMessage = async (content, file = null) => {
    if ((!content?.trim() && !file) || !selectedConversation?.id) return;

    setSending(true);
    try {
      if (file) {
        await sendMessageWithFile(selectedConversation?.id, user?.id, content, file);
      } else {
        await sendMessage(selectedConversation?.id, user?.id, content);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSendProposal = async (proposalData) => {
    if (!selectedConversation?.id) return;

    setSending(true);
    try {
      await sendMessage(
        selectedConversation?.id,
        user?.id,
        `Proposal: ${proposalData?.title || 'New Proposal'}`,
        true,
        proposalData
      );
    } catch (error) {
      console.error('Error sending proposal:', error);
    } finally {
      setSending(false);
    }
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setSearchQuery('');
    setSearchResults([]);
    setShowDetails(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6">
        <div className="bg-card border border-border rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
          <div className="flex h-full">
            {/* Conversation List Panel */}
            <div className="w-full md:w-96 border-r border-border flex flex-col">
              <ConversationList
                conversations={conversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleConversationSelect}
                currentUserId={user?.id}
              />
            </div>

            {/* Chat Interface Panel */}
            <div className="hidden md:flex flex-1 flex-col">
              {selectedConversation ? (
                <ChatInterface
                  conversation={selectedConversation}
                  messages={searchResults?.length > 0 ? searchResults : messages}
                  onSendMessage={handleSendMessage}
                  onSendProposal={handleSendProposal}
                  currentUserId={user?.id}
                  sending={sending}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onToggleDetails={() => setShowDetails(!showDetails)}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-center p-8">
                  <div>
                    <Icon name="MessageSquare" size={64} className="mx-auto mb-4 text-muted-foreground opacity-30" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">Select a conversation</h3>
                    <p className="text-muted-foreground">Choose a conversation from the list to start messaging</p>
                  </div>
                </div>
              )}
            </div>

            {/* Details Panel */}
            {showDetails && selectedConversation && (
              <div className="hidden lg:block w-80 border-l border-border">
                <ConversationDetails
                  conversation={selectedConversation}
                  messages={messages}
                  onClose={() => setShowDetails(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingHub;